import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { DatacloudPicklistStaticValue } from '../../models/appcloud/datacloud-picklist.model';

@Injectable({
    providedIn: 'root',
})
export class DatacloudPicklistStaticValueService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DATACLOUD_PICKLISTS_URL: string = environment.publicApiHost + '/appcloud/datacloud-picklists';

    findAll(
        picklistId: number,
        sortField?: string,
        sortDirection?: string,
        additionalHeaders: any = {}
    ): Observable<DatacloudPicklistStaticValue[]> {
        this.#spinnerService.addProcess(
            'DatacloudPicklistStaticValueService.findAll.' + picklistId,
            'Loading your picklist values...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('picklistId', picklistId.toString()),
        };

        if (sortField) {
            httpOptions.params = httpOptions.params.append('sortField', sortField);
        }

        if (sortDirection) {
            httpOptions.params = httpOptions.params.append('sortDirection', sortDirection);
        }

        return this.#http
            .get<
                DatacloudPicklistStaticValue[]
            >(`${this.DATACLOUD_PICKLISTS_URL}/${picklistId}/datacloud-picklist-static-values`, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving picklist values from Relationship One. Please try again later.'
                    );
                    return of<DatacloudPicklistStaticValue[]>();
                }),
                finalize(() =>
                    this.#spinnerService.completeProcess('DatacloudPicklistStaticValueService.findAll.' + picklistId)
                )
            );
    }

    create(
        picklistStaticValue: DatacloudPicklistStaticValue,
        additionalHeaders: any = {}
    ): Observable<DatacloudPicklistStaticValue> {
        this.#spinnerService.addProcess(
            'DatacloudPicklistStaticValueService.create',
            'Creating your datacloud picklist...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        const picklistId = picklistStaticValue.datacloudPicklistId;

        return this.#http
            .post<DatacloudPicklistStaticValue>(
                `${this.DATACLOUD_PICKLISTS_URL}/${picklistId}/datacloud-picklist-static-values`,
                picklistStaticValue,
                httpOptions
            )
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while saving your picklist value. Please try again later.'
                    );
                    return of<DatacloudPicklistStaticValue>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('DatacloudPicklistStaticValueService.create');
                })
            );
    }

    update(id: number, picklistStaticValue: DatacloudPicklistStaticValue, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess(
            'DatacloudPicklistStaticValueService.update.' + id,
            'Updating your picklist value...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };
        const picklistId = picklistStaticValue.datacloudPicklistId;

        return this.#http
            .patch<DatacloudPicklistStaticValue>(
                `${this.DATACLOUD_PICKLISTS_URL}/${picklistId}/datacloud-picklist-static-values/${id}`,
                picklistStaticValue,
                httpOptions
            )
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your picklist value. Please try again later.'
                    );
                    return of<DatacloudPicklistStaticValue>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudPicklistStaticValueService.update.' + id))
            );
    }

    delete(picklistId: number, id: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess(
            'DatacloudPicklistStaticValueService.delete.' + id,
            'Deleting your picklist value...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .delete<
                Observable<any>
            >(`${this.DATACLOUD_PICKLISTS_URL}/${picklistId}/datacloud-picklist-static-values/${id}`, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while deleting your picklist value. Try again later.'
                    );
                    return of<Observable<any>>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudPicklistStaticValueService.delete.' + id))
            );
    }
}
